/**
 * JSON Diff Utilities
 *
 * Extracted from HierarchicalMetadataExample for production use.
 * Used by FormSchemaOverrideDiffPanel to visualize base vs override diffs.
 *
 * Pure functions with zero external dependencies.
 */

// =============================================================================
// TYPES
// =============================================================================

export interface DiffNode {
  path: string;
  kind: 'added' | 'removed' | 'changed' | 'nested' | 'same';
  from?: unknown;
  to?: unknown;
  children?: DiffNode[];
}

export interface ShallowDiff {
  added: Record<string, unknown>;
  changed: Record<string, { from: unknown; to: unknown }>;
  removed: Record<string, unknown>;
}

// =============================================================================
// SHALLOW DIFF
// =============================================================================

/**
 * Compute a shallow (one-level) diff between two objects.
 * Returns added/changed/removed keys with their values.
 */
export function computeDiff(base: unknown, overlay: unknown): ShallowDiff | { error: string } {
  try {
    const b: Record<string, unknown> = typeof base === 'string' ? JSON.parse(base) : base || {};
    const o: Record<string, unknown> = typeof overlay === 'string' ? JSON.parse(overlay) : overlay || {};
    const diff: ShallowDiff = { added: {}, changed: {}, removed: {} };
    const bKeys = new Set(Object.keys(b || {}));
    const oKeys = new Set(Object.keys(o || {}));

    for (const k of oKeys) {
      if (!bKeys.has(k)) diff.added[k] = o[k];
    }
    for (const k of bKeys) {
      if (!oKeys.has(k)) diff.removed[k] = b[k];
    }
    for (const k of oKeys) {
      if (bKeys.has(k)) {
        const bs = JSON.stringify(b[k]);
        const os = JSON.stringify(o[k]);
        if (bs !== os) diff.changed[k] = { from: b[k], to: o[k] };
      }
    }
    return diff;
  } catch {
    return { error: 'Unable to diff' };
  }
}

// =============================================================================
// RECURSIVE DIFF
// =============================================================================

const isObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

const deepEqual = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/**
 * Build a deep recursive diff tree between two objects.
 * Each node reports its kind (added/removed/changed/nested/same)
 * and contains children for nested objects.
 */
export function buildRecursiveDiff(base: unknown, overlay: unknown, path: string = ''): DiffNode {
  const b = base ?? null;
  const o = overlay ?? null;

  if (!isObject(b) || !isObject(o)) {
    if (b === null && o !== null) return { path, kind: 'added', to: o };
    if (b !== null && o === null) return { path, kind: 'removed', from: b };
    if (!deepEqual(b, o)) return { path, kind: 'changed', from: b, to: o };
    return { path, kind: 'same', from: b, to: o };
  }

  const keys = Array.from(new Set([...Object.keys(b), ...Object.keys(o)])).sort();
  const children: DiffNode[] = keys.map(k =>
    buildRecursiveDiff(b[k], o[k], path ? `${path}.${k}` : k)
  );
  const hasChange = children.some(ch => ch.kind !== 'same');
  return { path, kind: hasChange ? 'nested' : 'same', children };
}

// =============================================================================
// DIFF STATISTICS
// =============================================================================

/**
 * Count diff nodes by kind (excluding 'same' and 'nested').
 */
export function countDiffChanges(node: DiffNode): { added: number; removed: number; changed: number } {
  const counts = { added: 0, removed: 0, changed: 0 };

  function walk(n: DiffNode) {
    if (n.kind === 'added') counts.added++;
    else if (n.kind === 'removed') counts.removed++;
    else if (n.kind === 'changed') counts.changed++;
    n.children?.forEach(walk);
  }

  walk(node);
  return counts;
}
